import React, { useState, useEffect } from 'react'
import { FormattedMessage } from 'react-intl'
import PropTypes from 'prop-types'
import { useLocation, useParams } from 'react-router-dom'
import { requestGetSite, requestValidateSite, requestUpdateSite, requestSiteVisibility, requestValidateSiteGeometry, requestValidateEditOnlySite, requestGetEditOnlySite } from 'api/site'
import Container from 'components/Container'
import { defaultSiteProposalValidationResults } from '../constants/defaultSiteProposalValidationResults'
import { showErrorToast } from 'utils/toast'
import { isEmpty } from 'lodash'


// Ensure all validation sections have defaults, especially when criterionE is missing
export const ensureValidationDefaults = (validation) => {
  const defaults = defaultSiteProposalValidationResults
  if (!validation.assessmentsAvailable && isEmpty(validation.criterionE)) {
    return {
      ...validation,
      assessmentParameters: validation.assessmentParameters || defaults.assessmentParameters,
      ecoSystem: validation.ecoSystem || defaults.ecoSystem,
      criterionC: validation.criterionC || defaults.criterionC,
      criterionE: {
        irreplaceability_assessment_approved: false,
        year: false,
        irreplaceability_score: false,
        trigger_reason_id: false,
        trigger_reason_text: false
      }
    }
  }
  return {
    ...validation,
    assessmentParameters: validation.assessmentParameters || defaults.assessmentParameters,
    ecoSystem: validation.ecoSystem || defaults.ecoSystem,
    criterionC: validation.criterionC || defaults.criterionC,
    criterionE: validation.criterionE || defaults.criterionE
  }
}

export const SiteContext = React.createContext({})

export const SiteProvider = ({ children }) => {
  const params = useParams()
  const location = useLocation()
  const isEditOnlyRoute = location.pathname.includes('/edit-only/')

  const [loadError, setLoadError] = useState(null)
  const [siteProposalFieldValidation, setSiteProposalFieldValidation] = useState(defaultSiteProposalValidationResults)
  const [triggerReasonId, setTriggerReasonId] = useState([])
  const [isEditable, setIsEditable] = useState(false)
  const defaultSite = { updatedAt: new Date(), name: "Static Monitoring Site" }

  const [site, setSite] = useState({
    CountryCode: null,
    ProposerId: '',
    SiteStatusId: null,
    createdAt: '',
    'EcoRegion.id': null,
    'EcoRegion.name': '',
    id: '',
    name: '',
    updatedAt: '',
    year: null,
    biologicalProcesses: [],
    conservationActionsNeeded: [],
    ecologicalIntegrity: false,
    geographicallyRestrictedBiodiversity: [],
    irreplaceabilityThroughQuantitiveAnalysis: false,
    ongoingConservationActions: [],
    reviewersContactsThatCanComment: null,
    threatenedBiodiversity: [],
    siteNameNational: '',
    state: '',
    siteArea: '',
    siteAreaCalculated: '',
    latitudeOfMidPoint: '',
    longitudeOfMidPoint: '',
    lowestAltitude: '',
    highestAltitude: '',
    rationaleForSiteInformation: '',
    siteDescription: '',
    sensitiveSite: false,
    sensitiveSiteDetails: '',
    howIsTheSiteManaged: '',
    deliniationRationale: '',
    boundary_source: '',
    coveredByWhatProtectedAreas: '',
    additionalBiodiversityValues: '',
    indigenousGroups: '',
    landUseRegimesAtSite: '',
    overlapOECM: '',
    notes: '',
    siteFiles: [],
    calculatedProtectedArea: '',
    pa_user_updated: null,
    pa_user_updated_justification: '',
    calculatedOECMArea: '',
    oecm_user_updated: null,
    oecm_user_updated_justification: '',
    CoverageOfProtectedAreasId: null,
    AltitudeOfBathymetryId: null,
    EcoSystemsId: null,
    EcoSystems2LargestId: null,
    EcoSystems3LargestId: null,
    EcoSystemsSmallestId: null,
    terrestrial_system: false,
    marine_system: false,
    freshwater_system: false,
    subterranean_system: false,
  })


  const getMySite = async () => {
    setLoadError(null)
    if (isEditOnlyRoute) {
      // Route is /site/:id/edit-only/:siteHistoryId — second param is the edit-only PK.
      // Do not fall back to params.id (parent site); that yields NOT_FOUND / access denied.
      const editOnlySiteId = params.siteHistoryId
      if (!editOnlySiteId) {
        setLoadError('siteProposalAccessDenied')
        return
      }
      const editOnlySite = await requestGetEditOnlySite(editOnlySiteId)
      if (editOnlySite?.error || editOnlySite?.errorCode || !editOnlySite?.id) {
        const message =
          editOnlySite?.message ||
          (typeof editOnlySite?.error === 'string' ? editOnlySite.error : null)
        setLoadError(message || 'siteProposalAccessDenied')
        return
      }
      return setSite({
        ...editOnlySite,
        dateOfAssesment: editOnlySite.dateOfAssesment,
        ecologicalIntegrity: editOnlySite.ecologicalIntegrity
      })
    }

    const site = await requestGetSite(params.id, params.siteHistoryId)
    if (site?.error) {
      const message = typeof site.error === 'string' ? site.error : site.message
      setLoadError(message || 'siteProposalAccessDenied')
      return
    }
    if (!site?.id && !site?.SiteId) {
      setLoadError('siteProposalAccessDenied')
      return
    }
    return setSite({ ...site, dateOfAssesment: site.dateOfAssesment, ecologicalIntegrity: site.ecologicalIntegrity })
  }

  const getMyEditOnlyApprovedSite = async () => {
    const site = await requestGetSite(null, params.siteHistoryId)
    return setSite({ ...site, dateOfAssesment: (site.dateOfAssesment), ecologicalIntegrity: site.ecologicalIntegrity })
  }

  const getIsSiteEditable = () => {
    requestSiteVisibility(params.id).then((data) => {
      if (data.error) {
        return showErrorToast(data.error, 'showBorder')
      }
      setIsEditable(data.permissions[0].edit)
    })
  }

  const getSiteValidation = async ({ showErrors = false } = {}) => {
    const [validateSiteRes, validateGeometryRes] = await Promise.all([
      requestValidateSite(params.id, params.siteHistoryId),
      requestValidateSiteGeometry(params.id, params.siteHistoryId)
    ]);

    let validation = { ...validateSiteRes };

    if (!validateGeometryRes.error) {
      validation = { ...validation, ...validateGeometryRes };
    }

    validation = ensureValidationDefaults(validation);

    let nextValidation;
    setSiteProposalFieldValidation((prev) => {
      nextValidation = {
        ...validation,
        updated: showErrors ? true : Boolean(prev?.updated)
      };
      return nextValidation;
    });
    return nextValidation;
  };

  const getEditOnlySiteValidation = async () => {
    const editOnlySiteId = params.siteHistoryId || params.id
    const validateSiteRes = await requestValidateEditOnlySite(editOnlySiteId)
    const validation = { ...validateSiteRes, assessmentParameters: defaultSiteProposalValidationResults.assessmentParameters }
    validation.assessmentsAvailable = true
    validation.about.proposalPurpose = true
    const nextValidation = { ...validation, updated: true }
    setSiteProposalFieldValidation(nextValidation)
    return nextValidation
  }


  const updateSite = (requestBody) => {
    requestUpdateSite(site.id, params.siteHistoryId, requestBody).then((response) => {
      setSite((s) => {
        return { ...s, ...response, dateOfAssesment: (response.dateOfAssesment), assessmentDate: (response.assessmentDate) }
      })
    })
  }

  const updateTriggerReasonId = (newTriggerReasonId) => {
    setTriggerReasonId(newTriggerReasonId)
  }


  useEffect(() => {
    getMySite()
    getIsSiteEditable()
  }, [])

  const contextValue = React.useMemo(() => ({
    site,
    setSite,
    isEditable,
    updateSite,
    siteProposalFieldValidation,
    setSiteProposalFieldValidation,
    getSiteValidation,
    getMySite,
    triggerReasonId,
    updateTriggerReasonId,
    getEditOnlySiteValidation,
    getMyEditOnlyApprovedSite
  }), [
    site,
    setSite,
    isEditable,
    updateSite,
    siteProposalFieldValidation,
    setSiteProposalFieldValidation,
    getSiteValidation,
    getMySite,
    triggerReasonId,
    updateTriggerReasonId,
    getEditOnlySiteValidation,
    getMyEditOnlyApprovedSite
  ])

  if (loadError) {
    return (
      <Container>
        <div style={{ padding: '32px', maxWidth: '640px', margin: '0 auto', fontSize: '16px', lineHeight: '1.5' }}>
          <FormattedMessage id="siteProposalAccessDenied" />
        </div>
      </Container>
    )
  }

  if (!site.id && !site.SiteId) {
    return null
  }
  return (
    <SiteContext.Provider value={contextValue}>
      {children}
    </SiteContext.Provider>
  )
}

export default SiteProvider

SiteProvider.propTypes = {
  children: PropTypes.node
}
